/**
 * Medications tab — categorized medication cards with interaction and prophylaxis alerts.
 */
import { FC, ReactElement } from 'react'
import {
  CircleCheck,
  Pause,
  RefreshCw,
  CircleX,
  AlertTriangle,
  ShieldAlert,
} from 'lucide-react'
import type { MedsStatus, PatientPanelData } from '../types'
import { MedicationCard } from '../shared/MedicationCard'
import { PanelSkeleton } from '../shared/PanelSkeleton'

/** Parse a medication string into name + optional rationale. */
function parseMedString(raw: string): { name: string; rationale?: string } {
  // Pattern: **MedName** rest of text
  const boldMatch = raw.match(/\*\*(.+?)\*\*(.*)/)
  if (boldMatch) {
    const name = boldMatch[1].trim()
    const rationale = boldMatch[2].replace(/^\s*[-\u2014\u2013]\s*/, '').trim()
    return { name, rationale: rationale || undefined }
  }

  // Pattern: Name — rationale (em dash)
  const dashParts = raw.split(/\s\u2014\s/)
  if (dashParts.length >= 2) {
    return { name: dashParts[0].trim(), rationale: dashParts.slice(1).join(' \u2014 ').trim() }
  }

  // Fallback: entire string as name
  return { name: raw.trim() }
}

interface MedCategoryConfig {
  key: 'continue' | 'hold' | 'adjust' | 'discontinue'
  label: string
  icon: FC<{ className?: string }>
  textColor: string
  badgeBg: string
}

const CATEGORIES: MedCategoryConfig[] = [
  { key: 'continue', label: 'Continue', icon: CircleCheck, textColor: 'text-clinical-teal', badgeBg: 'bg-green-500/10' },
  { key: 'hold', label: 'Hold', icon: Pause, textColor: 'text-clinical-amber', badgeBg: 'bg-amber-500/10' },
  { key: 'adjust', label: 'Adjust', icon: RefreshCw, textColor: 'text-primary', badgeBg: 'bg-blue-500/10' },
  { key: 'discontinue', label: 'Discontinue', icon: CircleX, textColor: 'text-clinical-coral', badgeBg: 'bg-red-500/10' },
]

/** The banner each status renders, `null` where silence is the decision.
 *
 *  A Record over the whole union, holding the banners themselves: adding a
 *  status to MedsStatus is then a compile error here, and — unlike a table of
 *  `true` flags read alongside a separate list of JSX — a status cannot end up
 *  listed as known while rendering nothing. The backend owns this vocabulary
 *  and can ship a value before the frontend learns it, so a status missing from
 *  the record at RUNTIME falls through to UNRECOGNIZED_BANNER: the dot
 *  (PatientPanel.medsDot) goes amber for exactly those, and a dot the clinician
 *  follows to an unexplained list is worse than no dot at all. */
const MEDS_STATUS_BANNERS: Record<NonNullable<MedsStatus>, ReactElement | null> = {
  reconciling: (
    <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg px-3 py-2 animate-pulse">
      <p className="text-xs font-medium text-primary">Reconciling medications...</p>
    </div>
  ),
  preliminary: (
    <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
      <p className="text-xs font-medium text-clinical-amber">
        Preliminary med recon — full reconciliation will run after A&amp;P is generated
      </p>
    </div>
  ),
  failed: (
    <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
      <p className="text-xs font-medium text-clinical-coral">
        Med recon did not complete — these medications are unreconciled; review manually.
        Saving the A&amp;P again will retry.
      </p>
    </div>
  ),
  partial: (
    <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
      <p className="text-xs font-medium text-clinical-amber">
        Medication reconciliation is incomplete — verify this list against the full chart
      </p>
    </div>
  ),
  // Deliberately silent: a finished reconciliation is the case the panel is
  // designed around, and a banner over every clean list is a banner nobody reads.
  reconciled: null,
}

const UNRECOGNIZED_BANNER = (
  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
    <p className="text-xs font-medium text-clinical-amber">
      Reconciliation state unrecognized — verify this list against the chart
    </p>
  </div>
)

/** The banner for a status, or null when silence is intended.
 *
 *  `null` means "no reconciliation state recorded" — every workspace predating
 *  the status column, and every hand-entered list — which is not an anomaly and
 *  gets no banner. Anything the record does not carry is one, and says so. */
function medsBanner(status: MedsStatus): ReactElement | null {
  if (status === null || status === undefined) return null
  return Object.prototype.hasOwnProperty.call(MEDS_STATUS_BANNERS, status)
    ? MEDS_STATUS_BANNERS[status]
    : UNRECOGNIZED_BANNER
}

export const MedsTab: FC<{ data: PatientPanelData }> = ({ data }) => {
  // Loading state
  if (data.isGenerating && !data.medications) {
    return (
      <div className="flex-1 overflow-y-auto custom-scrollbar p-3">
        <PanelSkeleton lines={6} />
      </div>
    )
  }

  // Empty state. The status banner comes FIRST: a workspace whose reconciliation
  // failed or fell short can legitimately have no medications at all, and the
  // tab dot goes red/amber for exactly that case — so returning the bare "no
  // data" line sent the clinician from a warning dot to a blank tab with no
  // explanation and no retry hint.
  if (!data.medications) {
    return (
      <div className="flex-1 overflow-y-auto custom-scrollbar p-3 space-y-4">
        {medsBanner(data.medsStatus)}
        <div className="flex items-center justify-center">
          <p className="text-xs text-muted-foreground opacity-40">
            No medication data available
          </p>
        </div>
      </div>
    )
  }

  const meds = data.medications

  return (
    <div className="flex-1 overflow-y-auto custom-scrollbar p-3 space-y-4">
      {/* Med-recon status banner (one table, no second source of truth) */}
      {medsBanner(data.medsStatus)}

      {/* Medication categories */}
      {CATEGORIES.map(({ key, label, icon: Icon, textColor, badgeBg }) => {
        const items = meds[key]
        if (!items || items.length === 0) return null

        return (
          <div key={key} className="space-y-1.5">
            <div className="flex items-center gap-2">
              <Icon className={`w-3.5 h-3.5 ${textColor}`} />
              <span className={`text-[10px] font-semibold uppercase tracking-wider ${textColor}`}>
                {label}
              </span>
              <span className={`text-[8px] font-bold ${textColor} ${badgeBg} px-1.5 py-0.5 rounded-full`}>
                {items.length}
              </span>
            </div>
            <div className="space-y-1.5">
              {items.map((item, i) => {
                const { name, rationale } = parseMedString(item)
                return <MedicationCard key={i} name={name} rationale={rationale} />
              })}
            </div>
          </div>
        )
      })}

      {/* Drug Interactions */}
      {meds.drug_interactions.length > 0 && (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5 text-clinical-amber" />
            <span className="text-[10px] font-semibold uppercase tracking-wider text-clinical-amber">
              Drug Interactions
            </span>
          </div>
          <div className="space-y-1.5">
            {meds.drug_interactions.map((item, i) => (
              <div
                key={i}
                className="bg-amber-500/5 border border-amber-500/10 rounded-lg px-3 py-2"
              >
                <p className="text-xs text-muted-foreground leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Prophylaxis Gaps */}
      {meds.prophylaxis_gaps.length > 0 && (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-3.5 h-3.5 text-primary" />
            <span className="text-[10px] font-semibold uppercase tracking-wider text-primary">
              Prophylaxis Gaps
            </span>
          </div>
          <div className="space-y-1.5">
            {meds.prophylaxis_gaps.map((item, i) => (
              <div
                key={i}
                className="bg-blue-500/5 border border-blue-500/10 rounded-lg px-3 py-2"
              >
                <p className="text-xs text-muted-foreground leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
